import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Upload,
  File,
  FileText,
  Image,
  Receipt,
  FileCheck,
  Trash2,
  Edit,
  Eye,
  EyeOff,
  Download,
  Loader2,
  FolderOpen,
  Link2,
  Send
} from "lucide-react";

const FILE_TYPES = [
  { value: "contract", label: "Contract", icon: FileCheck },
  { value: "invoice", label: "Invoice", icon: Receipt },
  { value: "photo", label: "Photo", icon: Image },
  { value: "estimate", label: "Estimate", icon: FileText },
  { value: "other", label: "Other", icon: File },
];

export default function ProjectDocumentsPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [project, setProject] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  
  // Portal access management
  const [portalAccess, setPortalAccess] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [sendingInvite, setSendingInvite] = useState(false);
  
  // Upload form
  const [uploadForm, setUploadForm] = useState({
    file: null,
    name: "",
    file_type: "other",
    description: "",
    visible_to_customer: true
  });

  useEffect(() => {
    fetchData();
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [projectRes, docsRes, accessRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`),
        axios.get(`${API}/projects/${projectId}/documents`),
        axios.get(`${API}/portal/access/${projectId}`).catch(() => ({ data: [] }))
      ]);
      setProject(projectRes.data);
      setDocuments(docsRes.data);
      setPortalAccess(accessRes.data);
    } catch (error) {
      toast.error("Failed to load project");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.file) {
      toast.error("Please select a file");
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadForm.file);
      formData.append("name", uploadForm.name || uploadForm.file.name);
      formData.append("file_type", uploadForm.file_type);
      formData.append("description", uploadForm.description);
      formData.append("visible_to_customer", uploadForm.visible_to_customer.toString());
      
      await axios.post(`${API}/projects/${projectId}/documents`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success("Document uploaded!");
      setShowUploadModal(false);
      setUploadForm({ file: null, name: "", file_type: "other", description: "", visible_to_customer: true });
      fetchData();
    } catch (error) {
      toast.error("Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingDoc) return;
    
    try {
      await axios.put(`${API}/projects/${projectId}/documents/${editingDoc.document_id}`, {
        name: editingDoc.name,
        description: editingDoc.description,
        file_type: editingDoc.file_type,
        visible_to_customer: editingDoc.visible_to_customer
      });
      toast.success("Document updated!");
      setEditingDoc(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update document");
    }
  };

  const handleDelete = async (docId) => {
    if (!confirm("Delete this document?")) return;
    
    try {
      await axios.delete(`${API}/projects/${projectId}/documents/${docId}`);
      toast.success("Document deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const handleSendPortalInvite = async () => {
    if (!inviteEmail) {
      toast.error("Please enter an email");
      return;
    }
    
    setSendingInvite(true);
    try {
      const response = await axios.post(`${API}/portal/invite`, {
        project_id: projectId,
        customer_email: inviteEmail,
        send_email: true
      });
      toast.success("Portal invite sent!");
      setShowInviteModal(false);
      setInviteEmail("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invite");
    } finally {
      setSendingInvite(false);
    }
  };

  const handleRevokeAccess = async (accessId) => {
    if (!confirm("Revoke this customer's portal access?")) return;
    
    try {
      await axios.delete(`${API}/portal/access/${accessId}`);
      toast.success("Access revoked");
      fetchData();
    } catch (error) {
      toast.error("Failed to revoke access");
    }
  };

  const copyPortalLink = (token) => {
    const url = `${window.location.origin}/portal/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Portal link copied to clipboard!");
  };

  const getFileIcon = (type) => {
    const fileType = FILE_TYPES.find(f => f.value === type);
    const Icon = fileType?.icon || File;
    return <Icon className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="project-documents-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{project?.name}</h1>
            <p className="text-slate-600">Document Center & Portal Management</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowInviteModal(true)}>
            <Send className="w-4 h-4 mr-2" />
            Invite Customer
          </Button>
          <Button onClick={() => setShowUploadModal(true)}>
            <Upload className="w-4 h-4 mr-2" />
            Upload Document
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Documents List */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FolderOpen className="w-5 h-5" />
                Project Documents
              </CardTitle>
            </CardHeader>
            <CardContent>
              {documents.length > 0 ? (
                <div className="space-y-3">
                  {documents.map(doc => (
                    <div key={doc.document_id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          doc.file_type === "contract" ? "bg-indigo-100 text-indigo-600" :
                          doc.file_type === "invoice" ? "bg-green-100 text-green-600" :
                          doc.file_type === "photo" ? "bg-blue-100 text-blue-600" :
                          "bg-slate-100 text-slate-600"
                        }`}>
                          {getFileIcon(doc.file_type)}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{doc.name}</p>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <span className="capitalize">{doc.file_type}</span>
                            <span>•</span>
                            <span>{(doc.file_size / 1024).toFixed(1)} KB</span>
                            <span>•</span>
                            <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                            {doc.visible_to_customer ? (
                              <Badge variant="outline" className="text-green-600 border-green-200">
                                <Eye className="w-3 h-3 mr-1" />
                                Visible
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-slate-500">
                                <EyeOff className="w-3 h-3 mr-1" />
                                Hidden
                              </Badge>
                            )}
                          </div>
                          {doc.description && (
                            <p className="text-xs text-slate-500 mt-1">{doc.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <a
                          href={`${API}${doc.file_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                        >
                          <Download className="w-4 h-4 text-slate-600" />
                        </a>
                        <Button variant="ghost" size="icon" onClick={() => setEditingDoc(doc)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDelete(doc.document_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <FolderOpen className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No documents uploaded yet</p>
                  <Button variant="outline" className="mt-4" onClick={() => setShowUploadModal(true)}>
                    Upload First Document
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Portal Access */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Link2 className="w-5 h-5" />
                Customer Portal Access
              </CardTitle>
            </CardHeader>
            <CardContent>
              {portalAccess.length > 0 ? (
                <div className="space-y-3">
                  {portalAccess.map(access => (
                    <div key={access.access_id} className={`p-3 rounded-lg ${access.is_active ? "bg-green-50" : "bg-slate-50"}`}>
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium text-sm">{access.customer_name}</p>
                        <Badge variant={access.is_active ? "default" : "secondary"}>
                          {access.is_active ? "Active" : "Revoked"}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500">{access.customer_email}</p>
                      {access.last_accessed && (
                        <p className="text-xs text-slate-400 mt-1">
                          Last visited: {new Date(access.last_accessed).toLocaleDateString()}
                        </p>
                      )}
                      {access.is_active && (
                        <div className="flex gap-2 mt-2">
                          <Button variant="outline" size="sm" onClick={() => copyPortalLink(access.access_token)}>
                            <Link2 className="w-3 h-3 mr-1" />
                            Copy Link
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleRevokeAccess(access.access_id)}>
                            Revoke
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-slate-500">
                  <p className="text-sm">No customer portal access</p>
                  <Button variant="outline" size="sm" className="mt-2" onClick={() => setShowInviteModal(true)}>
                    Invite Customer
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>File *</Label>
              <Input
                type="file"
                onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Document Name</Label>
              <Input
                value={uploadForm.name}
                onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                placeholder="Leave empty to use file name"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Type</Label>
              <Select
                value={uploadForm.file_type}
                onValueChange={(v) => setUploadForm({ ...uploadForm, file_type: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FILE_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                placeholder="Optional description"
                className="mt-1"
                rows={2}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={uploadForm.visible_to_customer}
                onCheckedChange={(v) => setUploadForm({ ...uploadForm, visible_to_customer: v })}
              />
              <Label>Visible to customer in portal</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadModal(false)}>Cancel</Button>
            <Button onClick={handleUpload} disabled={uploading}>
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={!!editingDoc} onOpenChange={() => setEditingDoc(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Document</DialogTitle>
          </DialogHeader>
          {editingDoc && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Document Name</Label>
                <Input
                  value={editingDoc.name}
                  onChange={(e) => setEditingDoc({ ...editingDoc, name: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Type</Label>
                <Select
                  value={editingDoc.file_type}
                  onValueChange={(v) => setEditingDoc({ ...editingDoc, file_type: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FILE_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={editingDoc.description || ""}
                  onChange={(e) => setEditingDoc({ ...editingDoc, description: e.target.value })}
                  className="mt-1"
                  rows={2}
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={editingDoc.visible_to_customer}
                  onCheckedChange={(v) => setEditingDoc({ ...editingDoc, visible_to_customer: v })}
                />
                <Label>Visible to customer in portal</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingDoc(null)}>Cancel</Button>
            <Button onClick={handleUpdate}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Customer Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Invite Customer to Portal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Send a portal invitation to your customer. They will receive an email with a link to view their project status, documents, and communicate with you.
            </p>
            <div>
              <Label>Customer Email *</Label>
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="customer@example.com"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteModal(false)}>Cancel</Button>
            <Button onClick={handleSendPortalInvite} disabled={sendingInvite}>
              {sendingInvite ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Send Invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
